import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { cn } from '@/lib/utils'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon, { LcIconName } from '@/components/livecontact/icons'
import {
  INPUT_ERROR_CLASS,
  INPUT_OPEN_CLASS,
  LABEL_CLASS,
  NOTE_CLASS,
  POP_CLASS,
} from '@/components/livecontact/fieldStyles'

export type MultiSelectItem = {
  title: string
  value: string
  description?: string
  color?: string
  disabled?: boolean
  icon?: LcIconName
}

type MultiSelectProps = {
  label?: string
  title?: string
  icon?: LcIconName
  className?: string
  classButton?: string
  classArrow?: string
  classOptions?: string
  classOption?: string
  classIcon?: string
  classLabel?: string
  placeholder?: string
  items?: MultiSelectItem[]
  value: MultiSelectItem[]
  onChange: (items: MultiSelectItem[]) => void
  small?: boolean
  up?: boolean
  maxDisplay?: number
  error?: HookFieldError
  note?: string
  showListIcon?: boolean
}

const Chip = ({ item }: { item: MultiSelectItem }) => (
  <span className="inline-flex h-6 items-center gap-1 rounded-full bg-canvas px-2 text-[12px] font-medium tracking-[-0.2px] text-ink-2 animate-lc-pop dark:bg-surface-3">
    {item.color && <span className="size-2 rounded-full" style={{ backgroundColor: item.color }} />}
    {item.icon && <LcIcon name={item.icon} size={12} className="text-ink-3" />}
    {item.title}
  </span>
)

/** Multi-select on the Select surface: chips in the trigger, checkbox rows in the pop list. */
const MultiSelect = ({
  label,
  title,
  icon,
  className,
  classButton,
  classArrow,
  classOptions,
  classOption,
  classIcon,
  classLabel,
  placeholder,
  items,
  value,
  onChange,
  small,
  up,
  maxDisplay = 3,
  note,
  error,
  showListIcon = true,
}: MultiSelectProps) => {
  const handleItemToggle = (item: MultiSelectItem) => {
    const isSelected = value.some((selectedItem) => selectedItem.value === item.value)
    if (isSelected) {
      onChange(value.filter((selectedItem) => selectedItem.value !== item.value))
    } else {
      onChange([...value, item])
    }
  }

  const renderOption = (item: MultiSelectItem) => {
    const isSelected = value.some((selectedItem) => selectedItem.value === item.value)

    return (
      <ListboxOption
        key={item.value}
        value={item}
        onClick={(e) => {
          e.preventDefault()
          handleItemToggle(item)
        }}
        className={cn(
          'flex cursor-pointer items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-[14px] font-medium leading-[1.2] tracking-[-0.2px] text-ink-2',
          'transition-colors duration-[120ms] ui-active:bg-surface-2 ui-active:text-ink dark:ui-active:bg-surface-3',
          small && 'py-1.5',
          isSelected && 'text-ink',
          item.disabled && 'cursor-not-allowed opacity-50',
          classOption
        )}
        disabled={item.disabled}
      >
        <span
          className={cn(
            'inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] transition-[background-color,box-shadow] duration-[140ms]',
            isSelected
              ? 'bg-brand text-brand-ink'
              : 'shadow-[inset_0_0_0_1.5px_rgb(var(--lc-line-2))]'
          )}
        >
          {isSelected && (
            <LcIcon name="check" size={10} strokeWidth={2.5} className="animate-lc-pop" />
          )}
        </span>
        {item.color && (
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: item.color }}
          />
        )}
        {item.icon && showListIcon && (
          <LcIcon name={item.icon} size={small ? 14 : 16} className="text-ink-3" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate">{item.title}</span>
          {item.description && (
            <span className="mt-0.5 block text-[12px] font-normal text-ink-4">
              {item.description}
            </span>
          )}
        </span>
      </ListboxOption>
    )
  }

  const renderSelectedItems = () => {
    if (value.length === 0) return <span className="text-ink-4">{placeholder}</span>

    if (value.length === 1) {
      const selectedItem = value[0]
      return (
        <span className="flex min-w-0 items-center gap-2">
          {selectedItem.color && (
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: selectedItem.color }}
            />
          )}
          {selectedItem.icon && <LcIcon name={selectedItem.icon} className="text-ink-3" />}
          <span className="truncate">{selectedItem.title}</span>
        </span>
      )
    }

    const visible = value.length <= maxDisplay ? value : value.slice(0, maxDisplay - 1)
    const overflow = value.length - visible.length

    return (
      <span className="flex min-w-0 flex-wrap items-center gap-1">
        {visible.map((item) => (
          <Chip key={item.value} item={item} />
        ))}
        {overflow > 0 && (
          <span className="text-[12px] font-medium text-ink-4">+{overflow} more</span>
        )}
      </span>
    )
  }

  return (
    <div className={cn('relative', className)}>
      {label && <div className={cn(LABEL_CLASS, classLabel)}>{label}</div>}
      <Listbox value={value} onChange={() => {}} multiple>
        {({ open }) => (
          <div>
            <ListboxButton
              className={cn(
                'flex w-full items-center rounded-[8px] bg-surface px-3 py-1.5 text-left text-[14px] leading-[1.4] tracking-[-0.2px] text-ink shadow-input outline-none',
                'transition-[box-shadow,transform] duration-[160ms] ease-out-strong active:scale-[0.995]',
                small ? 'min-h-8 px-2' : 'min-h-9',
                open && INPUT_OPEN_CLASS,
                error && INPUT_ERROR_CLASS,
                classButton
              )}
            >
              {title && (
                <span className="mr-2 shrink-0 border-r border-line pr-2 text-ink-4">{title}</span>
              )}
              {icon && (
                <LcIcon
                  name={icon}
                  className={cn('mr-2 text-ink-4', small && 'mr-1.5', classIcon)}
                />
              )}
              <span className={cn('mr-auto min-w-0 flex-1', small && 'font-medium')}>
                {renderSelectedItems()}
              </span>
              <LcIcon
                name="chevron-down"
                className={cn(
                  'ml-2 shrink-0 text-ink-4 transition-transform duration-[180ms] ease-out-strong',
                  open && 'rotate-180',
                  classArrow
                )}
              />
            </ListboxButton>

            <Transition
              leave="transition-opacity duration-[120ms] ease-out-strong"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions
                className={cn(
                  'absolute left-0 right-0 z-20 max-h-64 w-full overflow-y-auto outline-none lc-scrollbar-thin',
                  POP_CLASS,
                  'transition-[opacity,transform] duration-[180ms] ease-out-strong data-[closed]:scale-[0.97] data-[closed]:opacity-0',
                  up ? 'bottom-full mb-1.5 origin-bottom-left' : 'top-full mt-1.5 origin-top-left',
                  small && 'right-auto min-w-[220px]',
                  classOptions
                )}
              >
                {items?.map(renderOption)}
              </ListboxOptions>
            </Transition>
          </div>
        )}
      </Listbox>
      {note && <p className={NOTE_CLASS}>{note}</p>}
      <FieldError message={error?.message} />
    </div>
  )
}

export default MultiSelect
